import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFirebase } from "../../../../context/FirebaseContext";
import { getDoc, doc } from "firebase/firestore";
import { db, DEFAULT_STORE_ID } from "../../../../firebase/firebase";
import {
  updateOrderStatus,
} from "../../../../firebase/order-functions";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminOrdersTranslation } from "../translations";
import OrderHeader from "./components/OrderHeader";
import OrderInfoGrid from "./components/OrderInfoGrid";
import OrderItemsTable from "./components/OrderItemsTable";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorMessage from "./components/ErrorMessage";
import SuccessMessage from "./components/SuccessMessage";
import { formatCurrency, formatDate, formatPaymentMethod, getCustomerName } from "./utils/orderDetailsUtils";
import "./OrderDetailsView.css";

const OrderDetailsView = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useAdminOrdersTranslation(language);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      setError("");

      try {
        const orderRef = doc(
          db,
          "stores",
          DEFAULT_STORE_ID,
          "orders",
          orderId
        );
        const orderDoc = await getDoc(orderRef);

        if (!orderDoc.exists()) {
          setError(t('orderNotFound'));
          setLoading(false);
          return;
        }

        setOrder({
          id: orderDoc.id,
          ...orderDoc.data(),
        });
      } catch (err) {
        // console.error("Error fetching order details:", err);
        setError(t('failedToLoadOrderDetails'));
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  const handleStatusUpdate = async (newStatus) => {
    if (!order) return;

    setUpdating(true);
    setStatusUpdateSuccess(false);
    setError("");

    try {
      // console.log(`Updating order ${orderId} to status: ${newStatus}`);

      const idToUse = order.storeOrderId || orderId;
      const result = await updateOrderStatus(
        DEFAULT_STORE_ID,
        idToUse,
        newStatus
      );

      if (result.success) {
        setOrder({ ...order, status: newStatus });
        setStatusUpdateSuccess(true);

        // Email notifications are now handled automatically server-side
        // console.log("Order status updated - email notification will be sent automatically");

        setTimeout(() => {
          setStatusUpdateSuccess(false);
        }, 3000);
      } else {
        if (order.storeOrderId && idToUse === order.storeOrderId) {
          // console.log("First attempt failed, trying with original orderId as fallback");

          const fallbackResult = await updateOrderStatus(
            DEFAULT_STORE_ID,
            orderId,
            newStatus
          );

          if (fallbackResult.success) {
            setOrder({ ...order, status: newStatus });
            setStatusUpdateSuccess(true);

            // Email notifications are now handled automatically server-side
            console.log("Order status updated (fallback) - email notification will be sent automatically");

            setTimeout(() => {
              setStatusUpdateSuccess(false);
            }, 3000);
          } else {
            setError(t('failedToUpdateStatus', { message: fallbackResult.message }));
          }
        } else {
          setError(t('failedToUpdateStatus', { message: result.message }));
        }
      }
    } catch (err) {
      console.error("Error updating order status:", err);
      setError(t('failedToUpdateStatus', { message: err.message }));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorMessage
        error={error}
        onBackClick={() => navigate("/admin/orders")}
      />
    );
  }

  if (!order) {
    return (
      <ErrorMessage
        title={t('orderNotFound')}
        error={t('orderNotFound')}
        onBackClick={() => navigate("/admin/orders")}
      />
    );
  }

  return (
    <div className="order-details-container">
      <OrderHeader
        orderId={order.id}
        orderNumber={order.orderNumber}
        onBackClick={() => navigate("/admin/orders")}
        onInvoiceClick={() => navigate(`/admin/orders/${orderId}/invoice`)}
      />

      {statusUpdateSuccess && (
        <SuccessMessage message={t('orderStatusUpdated')} />
      )}

      <OrderInfoGrid
        order={order}
        updating={updating}
        onStatusUpdate={handleStatusUpdate}
      />

      <OrderItemsTable order={order} />
    </div>
  );
};

export default OrderDetailsView;